import React from 'react';
import PropTypes from 'prop-types';
import _omit from 'lodash/omit';
import cn from 'classnames';
import validations from '../validations';

/**
 * Validationable Input to be used with Form
 */

export default function createInput(Component) {
    class InputValidator extends React.Component {
        componentDidMount() {
            this.props.connectValidator(this);
        }

        static getDerivedStateFromProps(props) {
            if (props.serverError) {
                return {
                    errorMessage: props.serverError,
                    isValid: false,
                };
            }

            return null;
        }

        componentWillUnmount() {
            this.props.disconnectValidator(this);
        }

        state = {
            value: this.props.value,
            isValid: true,
        };

        validate() {
            if (this.props.required && !this.state.value) {
                return this.setValidation(false);
            } else if (!this.props.validations || !this.state.value) {
                return this.setValidation(true);
            } else {
                const isValid = this.runAllValidations();
                return this.setValidation(isValid);
            }
        }

        setValidation(condition) {
            this.setState({
                isValid: condition,
                errorMessage: condition
                    ? undefined
                    : this.getValidationErrorMsg(),
            });

            return condition;
        }

        getValidationErrorMsg() {
            if (typeof this.props.validationErrorMsg === 'function') {
                return this.props.validationErrorMsg(
                    this.state.isValid,
                    this.state.value
                );
            } else {
                return this.props.validationErrorMsg;
            }
        }

        runAllValidations() {
            return this.props.validations.split(',').every((validation) => {
                let args = validation.split(':');
                const validateFunc = args.shift();

                // turns string values into actual numbers
                args = args.map(JSON.parse);
                args = [this.state.value].concat(args);

                return (
                    validations[validateFunc] &&
                    validations[validateFunc](...args)
                );
            });
        }

        // Whenever the input changes we update the value state
        // of this component
        setValue(event) {
            const value = event.currentTarget.value;
            this.setState(
                {
                    value,
                },

                // When the value changes, wait for it to propagate and
                // then validate the input
                () => {
                    if (value) {
                        this.validate(this);
                    }
                }
            );
        }

        renderLabel() {
            if (this.props.label) {
                return (
                    <label htmlFor={this.props.id}>{this.props.label}</label>
                );
            }
        }

        render() {
            const props = _omit(this.props, ['validate', 'validations']);
            const divClass = cn(
                {
                    ['input--is-invalid']: !this.state.isValid,
                },
                this.props.classes
            );

            const spanClass = cn({
                ['input__sub-text__error']: !this.state.isValid,
            });

            return (
                <div className={divClass}>
                    {this.renderLabel()}
                    <Component
                        {...props}
                        onBlur={this.setValue.bind(this)}
                        onChange={this.setValue.bind(this)}
                        value={this.state.value}
                    />
                    {this.state.errorMessage ? (
                        <div className="input__sub-text">
                            <span className={spanClass}>
                                {this.state.errorMessage}
                            </span>
                        </div>
                    ) : null}
                </div>
            );
        }
    }

    InputValidator.propTypes = {
        validations: PropTypes.string, // comma separated string of validation functions
        validate: PropTypes.func,
        connectValidator: PropTypes.func,
        validationErrorMsg: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.func,
        ]),
        disconnectValidator: PropTypes.func,
        value: PropTypes.string,
        serverError: PropTypes.string,
        classes: PropTypes.string,
        required: PropTypes.bool,
        label: PropTypes.string,
        id: PropTypes.string.isRequired,
    };

    InputValidator.defaultProps = {
        inputType: 'text',
        value: '',
    };

    return InputValidator;
}
